import type * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { pageTransitionEntrance, pageTransitionExit } from '../../utils/animations';

export interface TransitionLayoutProps {
  children: React.ReactNode;
  location: string;
}

/**
 * TransitionLayout component.
 *
 * Wraps page content and triggers GSAP exit/entrance animations on route change.
 * The exit animation completes before the entrance animation begins, with a
 * combined total transition duration of no more than 600ms.
 *
 * Uses position: relative on the wrapper to prevent layout shift during transitions.
 *
 * Requirements: 6.3, 6.7
 */
const TransitionLayout: React.FC<TransitionLayoutProps> = ({ children, location }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [displayChildren, setDisplayChildren] = useState(children);
  const [currentLocation, setCurrentLocation] = useState(location);
  const isAnimating = useRef(false);

  const triggerTransition = useCallback(async () => {
    const wrapper = wrapperRef.current;
    if (!wrapper || isAnimating.current) return;

    isAnimating.current = true;

    try {
      // Step 1: Exit animation on old content
      await pageTransitionExit(wrapper);

      // Step 2: Jump to the top while the page is faded out (invisible), so the
      // scroll reset isn't seen. Gatsby's own scroll restoration is disabled
      // (shouldUpdateScroll) so this is the single, hidden scroll.
      window.scrollTo(0, 0);

      // Step 3: Mount new page content
      setDisplayChildren(children);

      // Step 4: Entrance animation on new content
      await pageTransitionEntrance(wrapper);
    } catch {
      // If animation fails, just show the new content
      setDisplayChildren(children);
      if (wrapper) {
        wrapper.style.opacity = '1';
        wrapper.style.transform = 'none';
      }
    } finally {
      isAnimating.current = false;
      setCurrentLocation(location);
    }
  }, [children, location]);

  useEffect(() => {
    if (location !== currentLocation) {
      triggerTransition();
    } else {
      // Same location — just update children directly (e.g., initial render)
      setDisplayChildren(children);
    }
  }, [location, children, currentLocation, triggerTransition]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '1px',
      }}
    >
      {displayChildren}
    </div>
  );
};

export default TransitionLayout;
